/**
 * Inline citations: `[3]`, `[1][3]` and `[1, 3]` in an answer point at the message's sources by number (the n-th
 * stored source, see messageSources.ts). A remark plugin turns the ones that resolve into links to `#cite-<n>`,
 * which the chat's `a` override renders as source popovers. Anything else stays exactly as written: numbers out
 * of range, `[0]`, code, and real links and their text.
 */

/** A bracket of citation numbers, comma-separated (no leading zeros: `[0]` and `[01]` aren't citations). */
const GROUP = /\[([1-9]\d*(?:\s*,\s*[1-9]\d*)*)\]/g;
const CITE_PREFIX = "#cite-";
const CITE_HREF = /^#cite-([1-9]\d*)$/;

export function citationHref(number: number): string {
  return `${CITE_PREFIX}${number}`;
}

/** The source number a `#cite-<n>` link points at; null for any other link. */
export function citationNumber(href: string | undefined): number | null {
  const match = href ? CITE_HREF.exec(href) : null;
  return match ? Number(match[1]) : null;
}

/** Plain text, or the number of a citation. */
export type CitationPart = string | number;

/**
 * Splits text into plain runs and citations, or null when nothing in it cites a source `has` knows. A bracket is
 * all or nothing: in `[2, 9]` with five sources, both numbers stay as written.
 */
export function splitCitations(text: string, has: (number: number) => boolean): CitationPart[] | null {
  const parts: CitationPart[] = [];
  let last = 0;
  for (const match of text.matchAll(GROUP)) {
    const numbers = match[1].split(",").map((n) => Number(n.trim()));
    if (!numbers.every((n) => has(n))) continue;
    const start = match.index ?? 0;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(...numbers);
    last = start + match[0].length;
  }
  if (last === 0) return null;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Minimal mdast shapes: only what the transform touches.
interface MdText {
  type: "text";
  value: string;
}
interface MdLink {
  type: "link";
  url: string;
  children: MdText[];
}
interface MdParent {
  type: string;
  children?: MdNode[];
}
type MdNode = MdText | MdLink | MdParent;

/** No citations inside these: links can't nest, and code shows as written. */
const SKIP = new Set(["link", "linkReference", "inlineCode", "code", "html"]);

function toNodes(parts: CitationPart[]): MdNode[] {
  return parts.map((part) =>
    typeof part === "number"
      ? { type: "link", url: citationHref(part), children: [{ type: "text", value: String(part) }] }
      : { type: "text", value: part },
  );
}

function transform(node: MdNode, has: (number: number) => boolean): void {
  if (!("children" in node) || !node.children || SKIP.has(node.type)) return;
  const next: MdNode[] = [];
  for (const child of node.children) {
    const parts = child.type === "text" ? splitCitations((child as MdText).value, has) : null;
    if (parts) {
      next.push(...toNodes(parts));
    } else {
      transform(child, has);
      next.push(child);
    }
  }
  node.children = next;
}

/**
 * remark plugin: citations become `#cite-<n>` links, one per number, for the sources `resolvable` marks: one
 * character per stored source, "1" where `[n]` can point at it (e.g. "1101"). The options stay plain JSON because
 * streamdown caches its processors by plugin name and serialized options: a function would read as `{}` and every
 * message would share the first one's processor.
 */
export function remarkCitations(options: { resolvable: string }) {
  const has = (number: number) => options.resolvable[number - 1] === "1";
  return (tree: MdNode) => transform(tree, has);
}
